import { useLazyQuery } from '@apollo/client';
import { gql } from '__generated__/gql';
import { FC, useEffect, useRef, useState } from 'react';

import ErrorMessage from 'components/ErrorMessage';
import { FormInput, FormPanel } from 'components/FormPanel';

import Alert from 'primitives/Alert';
import Button from 'primitives/Button';

const LOOKUP_USER_BY_EMAIL_QUERY = gql(`
  query LookupUserByEmailForUnitTransfer($email: String!) {
    lookupUserByEmail(email: $email) {
      id
      name
      maskedEmail
    }
  }
`);

export type TransfereeStepData = {
  transfereeUserId: string;
  transfereeName: string;
  transfereeMaskedEmail: string;
};

const StepTransferee: FC<{
  data: TransfereeStepData;
  onSubmit: (data: TransfereeStepData) => void;
  onReadyChange: (ready: boolean) => void;
  buttonRef: any;
}> = ({ data, onSubmit, onReadyChange, buttonRef }) => {
  const [notFound, setNotFound] = useState(false);
  const [confirmed, setConfirmed] = useState<TransfereeStepData | null>(
    data.transfereeUserId
      ? {
          transfereeUserId: data.transfereeUserId,
          transfereeName: data.transfereeName,
          transfereeMaskedEmail: data.transfereeMaskedEmail,
        }
      : null
  );

  // The wizard's Next button and this step's own "Look up" button drive two different actions,
  // so the FormPanel gets its own ref: "Look up" runs the panel's validation + submit, while the
  // wizard's ref only ever advances an already-confirmed transferee.
  const formPanelRef = useRef<any>(null);

  const [lookupUser, { loading, error }] = useLazyQuery(LOOKUP_USER_BY_EMAIL_QUERY, {
    fetchPolicy: 'network-only',
    onCompleted: result => {
      if (result.lookupUserByEmail) {
        setNotFound(false);
        setConfirmed({
          transfereeUserId: result.lookupUserByEmail.id,
          transfereeName: result.lookupUserByEmail.name,
          transfereeMaskedEmail: result.lookupUserByEmail.maskedEmail,
        });
      } else {
        setNotFound(true);
        setConfirmed(null);
      }
    },
  });

  useEffect(() => {
    onReadyChange(!!confirmed);
  }, [confirmed, onReadyChange]);

  useEffect(() => {
    if (buttonRef) {
      buttonRef.current = {
        submit: () => {
          if (confirmed) onSubmit(confirmed);
        },
      };
    }
  }, [buttonRef, confirmed, onSubmit]);

  function clearConfirmedTransferee() {
    setNotFound(false);
    setConfirmed(null);
  }

  return (
    <div>
      <p className="text-sm text-gray-600 mb-4">
        The transferee must already have an account on AngelList India. Enter their email to look
        them up — this is limited to 20 lookups per hour.
      </p>

      <FormPanel
        loading={loading}
        error={undefined}
        buttonRef={formPanelRef}
        onSubmit={formData => lookupUser({ variables: { email: formData.transfereeEmail } })}
      >
        <FormInput
          fullWidth
          type="text"
          fieldName="transfereeEmail"
          label="Transferee's email address *"
          helperText="We'll show you their name to confirm before you continue"
          defaultValue=""
          onChange={clearConfirmedTransferee}
          validators={{ required: true, isEmail: true }}
        />
      </FormPanel>

      <div className="mt-3">
        <Button loading={loading} onClick={() => formPanelRef.current?.submit()}>
          Look up
        </Button>
      </div>

      {error ? <ErrorMessage error={error} type="alert" /> : null}

      {notFound && !loading ? (
        <Alert
          className="mt-4"
          type="error"
          title="No account found with that email"
          message="The transferee must already be on the platform before you can transfer units to them."
        />
      ) : null}

      {confirmed ? (
        <Alert
          className="mt-4"
          type="success"
          title={confirmed.transfereeName}
          message={confirmed.transfereeMaskedEmail}
        />
      ) : (
        <p className="mt-4 text-xs text-gray-400">
          Look up and confirm a transferee before continuing.
        </p>
      )}
    </div>
  );
};

export default StepTransferee;
